import { Type } from "@sinclair/typebox";
import { PresentationViewSchema } from "./presentation.js";
import { IdSchema, Sha256Schema, TimestampSchema } from "./shared.js";
import { ResearchRequirementDefinitionSchema, ResearchRequirementSchema } from "./research.js";
const label = Type.String({ minLength: 1, maxLength: 300 });
const prose = Type.String({ minLength: 1, maxLength: 64 * 1024 });
export const ReportNodeSchema = Type.Union([
    Type.Object({
        kind: Type.Literal("fact"),
        observationId: IdSchema,
        source: Type.Union([Type.Literal("result"), Type.Literal("arguments")]),
        sourcePath: Type.String({ minLength: 1, maxLength: 512, pattern: "^/" }),
        title: label,
        operation: Type.Union([Type.Literal("value"), Type.Literal("length")]),
    }, { additionalProperties: false }),
    Type.Object({ kind: Type.Literal("markdown"), markdown: prose }, { additionalProperties: false }),
    Type.Object({
        kind: Type.Literal("evidence"),
        observationId: IdSchema,
        view: PresentationViewSchema,
        caption: prose,
        purpose: Type.String({ minLength: 1, maxLength: 4000 }),
    }, { additionalProperties: false }),
    Type.Object({
        kind: Type.Literal("excerpt"),
        observationId: IdSchema,
        sourcePath: Type.String({ minLength: 1, maxLength: 512, pattern: "^/" }),
        title: label,
    }, { additionalProperties: false }),
    Type.Object({ kind: Type.Literal("artifact"), artifactId: IdSchema, caption: prose }, { additionalProperties: false }),
]);
/** 章节数量由任务决定；总字节、模型预算与浏览器资源另行约束。 */
export const ReportPlanSchema = Type.Object({
    title: label,
    sections: Type.Array(Type.Object({
        sectionId: IdSchema,
        title: label,
        layout: Type.Union([
            Type.Literal("stack"),
            Type.Literal("compare"),
            Type.Literal("tabs"),
        ]),
        role: Type.Union([
            Type.Literal("finding"),
            Type.Literal("method"),
            Type.Literal("appendix"),
        ]),
        nodes: Type.Array(ReportNodeSchema, { minItems: 1 }),
    }, { additionalProperties: false }), { minItems: 1 }),
}, { additionalProperties: false });
/** 缺口只描述原目标的证据要求，不携带动作、函数或授权参数。 */
export const ReportEvidenceGapSchema = Type.Object({
    sourceTurnId: IdSchema,
    requirementId: IdSchema,
    requirement: Type.String({ minLength: 1, maxLength: 4000 }),
    reason: Type.String({ minLength: 1, maxLength: 4000 }),
}, { additionalProperties: false });
export const ReportIssueCategorySchema = Type.Union([
    Type.Literal("content_error"),
    Type.Literal("missing_evidence"),
    Type.Literal("presentation_error"),
    Type.Literal("unsupported_capability"),
]);
export const ReportIssueSchema = Type.Object({
    category: Type.Optional(ReportIssueCategorySchema),
    evidenceGap: Type.Optional(ReportEvidenceGapSchema),
    sectionId: Type.Union([IdSchema, Type.Null()]),
    severity: Type.Union([Type.Literal("error"), Type.Literal("warning")]),
    message: Type.String({ minLength: 1, maxLength: 4000 }),
    remedy: Type.String({ maxLength: 4000 }),
}, { additionalProperties: false });
export const ReportReviewSchema = Type.Object({
    verdict: Type.Union([Type.Literal("approve"), Type.Literal("rework")]),
    issues: Type.Array(ReportIssueSchema),
}, { additionalProperties: false });
/** 报告内容仅核验实际主张，不携带研究需求或补证授权。 */
export const ReportContentReviewSchema = Type.Object({
    verdict: ReportReviewSchema.properties.verdict,
    issues: Type.Array(Type.Object({
        category: Type.Literal("content_error"),
        sectionId: ReportIssueSchema.properties.sectionId,
        severity: ReportIssueSchema.properties.severity,
        message: ReportIssueSchema.properties.message,
        remedy: ReportIssueSchema.properties.remedy,
    }, { additionalProperties: false })),
}, { additionalProperties: false });
/** 冻结来源只登记身份与摘要；原始结果仍由 ObservationStore 提供。 */
export const ReportSourceSchema = Type.Object({
    goals: Type.Array(Type.Object({
        turnId: IdSchema,
        goal: Type.String({ minLength: 1, maxLength: 100000 }),
        summary: prose,
        requirementDefinitions: Type.Optional(Type.Array(ResearchRequirementDefinitionSchema, { minItems: 1, maxItems: 64 })),
        requirements: Type.Optional(Type.Array(ResearchRequirementSchema, { minItems: 1, maxItems: 64 })),
    }, { additionalProperties: false }), { minItems: 1 }),
    observations: Type.Array(Type.Object({
        turnId: IdSchema,
        observationId: IdSchema,
        actionId: IdSchema,
        resultDigest: Sha256Schema,
    }, { additionalProperties: false })),
}, { additionalProperties: false });
export const ReportScreenshotSchema = Type.Object({
    sectionId: IdSchema,
    scope: Type.Union([Type.Literal("section"), Type.Literal("header"), Type.Literal("footer")]),
    partIndex: Type.Integer({ minimum: 0 }),
    partCount: Type.Integer({ minimum: 1 }),
    viewport: Type.Union([Type.Literal("desktop"), Type.Literal("mobile"), Type.Literal("print")]),
    path: Type.String({ pattern: "^result/", maxLength: 4096 }),
    digest: Sha256Schema,
    sizeBytes: Type.Integer({ minimum: 1, maximum: 32 * 1024 * 1024 }),
}, { additionalProperties: false });
export const ReportRenderResultSchema = Type.Object({
    htmlDigest: Sha256Schema,
    themeDigest: Sha256Schema,
    screenshots: Type.Array(ReportScreenshotSchema),
    issues: Type.Array(ReportIssueSchema),
}, { additionalProperties: false });
export const ReportRenderArgumentsSchema = Type.Object({
    htmlDigest: Sha256Schema,
    themeDigest: Sha256Schema,
    outputDirectory: Type.String({ pattern: "^result/report_[A-Za-z0-9_-]+$", maxLength: 256 }),
    maximumOutputBytes: Type.Integer({ minimum: 1, maximum: 256 * 1024 * 1024 }),
}, { additionalProperties: false });
export const ReportRenderParametersSchema = Type.Object({
    ...ReportRenderArgumentsSchema.properties,
    html: Type.String({ minLength: 1, maxLength: 768 * 1024 }),
    actionGrantId: IdSchema,
    actionRequestDigest: Sha256Schema,
}, { additionalProperties: false });
export const ReportMetadataSchema = Type.Object({
    reportId: Type.Optional(IdSchema),
    sourceTurnIds: Type.Optional(Type.Array(IdSchema, { uniqueItems: true })),
    sourceDigest: Type.Optional(Sha256Schema),
    attemptRevision: Type.Optional(Type.Integer({ minimum: 1 })),
    failure: Type.Optional(Type.Object({
        stage: Type.Union([
            Type.Literal("authoring"),
            Type.Literal("content"),
            Type.Literal("rendering"),
            Type.Literal("vision"),
        ]),
        code: Type.Union([
            Type.Literal("deadline_exceeded"),
            Type.Literal("budget_exceeded"),
            Type.Literal("model_unavailable"),
            Type.Literal("render_failed"),
            Type.Literal("content_invalid"),
            Type.Literal("binding_mismatch"),
            Type.Literal("cancelled"),
            Type.Literal("no_progress"),
            Type.Literal("evidence_missing"),
            Type.Literal("capability_unavailable"),
        ]),
        retryable: Type.Boolean(),
    }, { additionalProperties: false })),
    contentDigest: Type.Optional(Sha256Schema),
    evidenceGaps: Type.Optional(Type.Array(ReportEvidenceGapSchema, { minItems: 1, maxItems: 64 })),
    /** 仅服务端手动重试接纳后设置；新证据修订必须清除。 */
    evidenceRetryAuthorized: Type.Optional(Type.Boolean()),
    authoringRound: Type.Optional(Type.Integer({ minimum: 0 })),
    lastErrorDigest: Type.Optional(Sha256Schema),
    unchangedErrorCount: Type.Optional(Type.Integer({ minimum: 0 })),
    contentReview: Type.Optional(ReportReviewSchema),
    reviewBatches: Type.Optional(Type.Array(Type.Object({ key: Sha256Schema, review: ReportReviewSchema }, { additionalProperties: false }))),
    plan: Type.Optional(ReportPlanSchema),
    reviewDeadline: Type.Optional(TimestampSchema),
    visionDeadline: Type.Optional(TimestampSchema),
    generatedAt: TimestampSchema,
    updatedAt: TimestampSchema,
    approvedAt: Type.Optional(TimestampSchema),
    reviewPolicyDigest: Type.Optional(Sha256Schema),
    themeDigest: Sha256Schema,
    evidenceDigest: Sha256Schema,
    state: Type.Union([
        Type.Literal("draft"),
        Type.Literal("reviewing"),
        Type.Literal("revising"),
        Type.Literal("approved"),
        Type.Literal("unapproved"),
    ]),
    sections: Type.Array(Type.Object({
        sectionId: IdSchema,
        title: label,
        layout: Type.Union([
            Type.Literal("stack"),
            Type.Literal("compare"),
            Type.Literal("tabs"),
        ]),
        role: Type.Union([
            Type.Literal("finding"),
            Type.Literal("method"),
            Type.Literal("appendix"),
        ]),
        blockIndices: Type.Array(Type.Integer({ minimum: 0 })),
        captions: Type.Array(Type.String({ maxLength: 64 * 1024 })),
    }, { additionalProperties: false })),
    issues: Type.Array(ReportIssueSchema),
    reviewRound: Type.Integer({ minimum: 0 }),
    htmlPath: Type.Optional(Type.String({ pattern: "^result/", maxLength: 4096 })),
    htmlDigest: Type.Optional(Sha256Schema),
    reviewedDigest: Type.Optional(Sha256Schema),
}, { additionalProperties: false });
export const ReportReplySchema = Type.Object({
    response: prose,
    reportPlan: Type.Optional(ReportPlanSchema),
}, { additionalProperties: false });
/** iframe 只发送语义引用；宿主仍按当前报告解析文件与所选数据。 */
export const ReportPreviewMessageSchema = Type.Union([
    Type.Object({ kind: Type.Literal("report_resize"), height: Type.Integer({ minimum: 1, maximum: 1000000 }) }, { additionalProperties: false }),
    Type.Object({ kind: Type.Literal("report_artifact"), artifactId: IdSchema }, { additionalProperties: false }),
    Type.Object({
        kind: Type.Literal("report_selection"),
        blockIndex: Type.Integer({ minimum: 0 }),
        rowIndex: Type.Integer({ minimum: 0 }),
        columnIndex: Type.Optional(Type.Integer({ minimum: 0 })),
    }, { additionalProperties: false }),
]);
//# sourceMappingURL=report.js.map